import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  GoogleApiWrapper,
  InfoWindow,
  Map,
  Marker,
} from 'google-maps-react';
import {
  debounce,
  find,
  get,
  isEmpty,
  map,
  noop,
  orderBy,
} from 'lodash';
import {
  formatAddress,
  formatEmail,
  formatPhoneNumber,
  getProviderLatLng,
} from '../../utils';

import { DAYS_OF_THE_WEEK, CLOSED_DAY } from '../HoursOfOperation/constants';
import { formatDuration } from '../HoursOfOperation/utils';
import markerImage from './MarkerImage';
import {
  IconClock,
  IconMail,
  IconMarker,
  IconPhone,
} from '../Icons';

export class MapContainerComp extends Component {
  constructor(props) {
    super(props);

    const today = new Date();
    const lat = get(window, 'uuSearchSettings.map-center-lat', '');
    const lng = get(window, 'uuSearchSettings.map-center-long', '');
    const initialZoom = get(window, 'uuSearchSettings.google-map-zoom', '');

    this.state = {
      selectedMarker: {},
      markerToAnimate: null,
      center: {
        lat: isEmpty(lat) ? 40.720195 : parseFloat(lat),
        lng: isEmpty(lng) ? 40.720195 : parseFloat(lng),
      },
      initialZoom: isEmpty(initialZoom) ? 12 : parseInt(initialZoom, 10),
      today: DAYS_OF_THE_WEEK[today.getDay()],
    };

    this.onMarkerClick = this.onMarkerClick.bind(this);
    this.onMarkerHover = this.onMarkerHover.bind(this);
    this.onMarkerMouseOut = this.onMarkerMouseOut.bind(this);
    this.onInfoWindowClose = this.onInfoWindowClose.bind(this);
    this.animateMarker = this.animateMarker.bind(this);
    this.debouncedAnimateMarker = debounce(this.animateMarker, 300);
    this.onCenterChanged = this.onCenterChanged.bind(this);
    this.debouncedOnCenterChanged = debounce(this.onCenterChanged, 300);
  }

  componentWillReceiveProps(nextProps) {
    const {
      hoveredCard,
      selectedProvider,
    } = this.props;

    const { center } = this.state;

    if (hoveredCard !== nextProps.hoveredCard) {
      this.debouncedAnimateMarker(nextProps.hoveredCard);
      const newCenter = getProviderLatLng(nextProps.hoveredCard, center);
      this.setState({
        center: newCenter,
      });
    }
    if (nextProps.selectedProvider && selectedProvider !== nextProps.selectedProvider) {
      const newCenter = getProviderLatLng(nextProps.selectedProvider, center);
      this.setState({
        center: newCenter,
      });
    }
  }

  shouldComponentUpdate(nextProps) {
    const { hoveredCard } = this.props;
    if (hoveredCard !== nextProps.hoveredCard) {
      return false;
    }
    return true;
  }

  onMarkerClick(place, marker) {
    const { onMarkerClick } = this.props;
    this.setState({ selectedMarker: marker });
    onMarkerClick(place, marker);
  }

  onMarkerHover(place, marker) {
    const { onMarkerHover } = this.props;
    onMarkerHover(place, marker);
  }

  onMarkerMouseOut(place, marker) {
    const { onMarkerMouseOut } = this.props;
    onMarkerMouseOut(place, marker);
  }

  onInfoWindowClose(event) {
    const { onInfoWindowClose } = this.props;
    onInfoWindowClose(event);
    this.setState({ selectedMarker: {} });
  }

  onCenterChanged(mapProps, googleMap) {
    const { onMapCenterChange } = this.props;
    onMapCenterChange(mapProps, googleMap);
  }

  animateMarker(provider) {
    this.setState({ markerToAnimate: get(provider, 'id', null) });
  }

  render() {
    const {
      google,
      mapBounds,
      onMapReady,
      providers,
      selectedProvider,
      showInfoWindow,
    } = this.props;

    const {
      center,
      initialZoom,
      markerToAnimate,
      selectedMarker,
      today,
    } = this.state;

    const markerColor = get(window, 'uuSearchSettings.markers-color') || 'red';
    const hasPhone = !isEmpty(selectedProvider.phone_numbers);
    const hasEmail = !isEmpty(selectedProvider.email_addresses);
    const hasHoursOfOperation = !isEmpty(selectedProvider.hours_of_operation);
    const { hours_of_operation } = find(selectedProvider.hours_of_operation, { day_of_week: today }) || {};
    const hoursDisplay = map(
      orderBy(hours_of_operation, 'opens_at'),
      formatDuration,
    ).join(', ');
    const hasResults = !isEmpty(providers);

    return (
      <div className={`uu-map-container ${hasResults ? '' : 'no-results'}`}>
        <Map
          bounds={mapBounds}
          center={center}
          google={google}
          initialCenter={center}
          onCenter_changed={this.debouncedOnCenterChanged}
          onReady={onMapReady}
          visible={hasResults}
          zoom={initialZoom}
        >
          {
            providers.map((provider) => {
              const iconSize = markerToAnimate === provider.id ? 30 : 20;
              return (
                <Marker
                  icon={{
                    url: markerImage(markerColor),
                    anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
                    scaledSize: new google.maps.Size(iconSize, iconSize),
                  }}
                  key={provider.id}
                  name={provider.name}
                  onBlur={noop}
                  onClick={this.onMarkerClick}
                  onFocus={noop}
                  onMouseout={this.onMarkerMouseOut}
                  onMouseover={this.onMarkerHover}
                  position={getProviderLatLng(provider)}
                  provider={provider}
                />
              );
            })
          }

          <InfoWindow
            onClose={this.onInfoWindowClose}
            marker={selectedMarker}
            visible={showInfoWindow}
          >
            <div className="uu-infowindow-container">
              <h5>{selectedProvider.name}</h5>
              <div className="uu-infowindow__address">
                <IconMarker />
                <span className="uu-infowindow__phone-number">
                  {formatAddress(get(selectedProvider, 'addresses[0]'))}
                </span>
              </div>
              {
                hasHoursOfOperation && (
                  <div className="uu-infowindow__hoo">
                    <IconClock />
                    <span>TODAY:&nbsp;</span>
                    <span>{isEmpty(hours_of_operation) ? CLOSED_DAY : hoursDisplay}</span>
                  </div>
                )
              }
              {
                hasPhone && (
                  <div className="uu-infowindow__phone">
                    <IconPhone />
                    <span className="uu-infowindow__phone-number">
                      {formatPhoneNumber(selectedProvider.phone_numbers)}
                    </span>
                  </div>
                )
              }
              {
                hasEmail && (
                  <div className="uu-infowindow__email">
                    <IconMail />
                    <a
                      href={`mailto:${formatEmail(selectedProvider.email_addresses)}`}
                    >
                      {formatEmail(selectedProvider.email_addresses)}
                    </a>
                  </div>
                )
              }
            </div>
          </InfoWindow>
        </Map>
      </div>
    );
  }
}

MapContainerComp.propTypes = {
  google: PropTypes.object.isRequired,
  hoveredCard: PropTypes.object,
  mapBounds: PropTypes.object,
  onInfoWindowClose: PropTypes.func.isRequired,
  onMapCenterChange: PropTypes.func.isRequired,
  onMapReady: PropTypes.func.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  onMarkerHover: PropTypes.func,
  onMarkerMouseOut: PropTypes.func,
  providers: PropTypes.array.isRequired,
  selectedProvider: PropTypes.object.isRequired,
  showInfoWindow: PropTypes.bool.isRequired,
};

MapContainerComp.defaultProps = {
  hoveredCard: null,
  mapBounds: null,
  onMarkerHover: noop,
  onMarkerMouseOut: noop,
};

export default GoogleApiWrapper(props => ({
  apiKey: props.apiKey,
}))(MapContainerComp);



// WEBPACK FOOTER //
// ./public/js/components/Map/index.jsx